import React, { useState } from "react";
import Grid from "@material-ui/core/Grid";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { useForm, Controller } from "react-hook-form";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import Checkbox from "@material-ui/core/Checkbox";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import Autocomplete from "@material-ui/lab/Autocomplete";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/rootReducer";
import api from "../../../store/api";
import { differenceInMonths } from "date-fns";
import submitData from "../submitData";
import { showBookingToast } from "../../../store/modules/appStateSlice";
import { useHistory } from "react-router-dom";
import DateFnsUtils from "@date-io/date-fns";
import { RepeatIcon } from "../../../assets/iconComponents/Icons";
import CircularProgress from "@material-ui/core/CircularProgress";
import throttle from "lodash/throttle";
import { consts } from "../../../common/consts";
import _ from "lodash";
import { HorizontalSplit } from "@material-ui/icons";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    paper: {
      padding: theme.spacing(0),
      textAlign: "center",
      color: theme.palette.text.secondary,
    },
    listItemTextPrimary: {
      fontSize: 46,
      fontWeight: 700,
    },
    listItemTextSecondary: {
      fontSize: 24,
      fontWeight: 700,
      color: "#333333",
    },
    needsPoint: {
      fontSize: 19,
      fontWeight: 400,
      color: "#333333",
    },
    badge: {
      borderRadius: 0,
    },
    submitButtonRoot: {
      backgroundColor: "#02489D",
      borderRadius: 6,
      padding: "15px 35px",
      "&:hover": {
        backgroundColor: "#002d63",
      },
    },
    submitButtonLabel: {
      textTransform: "capitalize",
      fontSize: 18,
      fontWeight: 400,
      color: "#FFF",
    },
    formControlLabel: {
      fontSize: 18,
      color: "#4A4A4A",
      padding: theme.spacing(0, 0, 1, 0),
    },
    autoCompleteRoot: {
      "& .MuiFormControl-root": {
        margin: 0,
      },
      "& .MuiInputBase-root": {
        padding: 0,
      },
      "& .MuiAutocomplete-input": {
        padding: "9.5px 14px !important",
      },
    },
    namePosSection: {
      margin: theme.spacing(3, 0),
    },
  })
);

const getReason = (reasonForThisTime: string, otherReasonDetails: any) => {
  switch (reasonForThisTime) {
    case "1":
      return "Patient has an appointment";
    case "2":
      return "Patient has an all-day appointment (needs morning transfer)";
    case "3":
      return "Patient being discharged and their bed is needed";
    case "4":
      return "Bed not available at destination until this time";
    case "5":
      return otherReasonDetails ? otherReasonDetails : "Other reason";
  }
};

function _calculateAge(birthday: Date) {
  let months = differenceInMonths(new Date(), birthday);

  return Math.floor(months / 12);
}

function _calculateDay(date: Date) {
  const weekDay = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  if (new Date().getDate() == new Date(date).getDate()) return "Today";
  else if (new Date().getDate() + 1 == new Date(date).getDate()) return "Tommorrow";
  else return weekDay[new Date(date).getDay()];
}

const parseTime = (time: any): string => {
  if (time === null || time === "undefined") return "";

  const hr = _.get(time, "hours");
  const mins = _.get(time, "minutes");

  if (hr !== undefined && mins !== undefined) {
    return `${("0" + hr).slice(-2)}:${("0" + mins).slice(-2)}`;
  }

  let d = new Date(time);
  return `${("0" + d.getHours()).slice(-2)}:${("0" + d.getMinutes()).slice(-2)}`;
};

interface SelectItem extends React.Props<any> {
  text: string | undefined;
  value: string | undefined;
  selected: boolean;
}

interface DoctorNamesType {
  name: string|null,
  title: string | null,
};

const ReviewAndSubmit = ({ NextButton, handleData, data, saveChangesState, dispatch }: any) => {
  const dateUtil = new DateFnsUtils();
  const history = useHistory();
  const preloadedValues = {
    DeclarationFirstName: "",
    DeclarationLastName: "",
    DeclarationPhone: "",
    DeclarationAHPRANumber: "",
    confirmInfo: false,
    authorisedDoctor: false,
    attendingReferringDoctor: "",
    doctorPosition: "",
  };
  const [doctorNames, setDoctorNames] = React.useState<String[]>([]);
  const [loadingDoctorNames, setLoadingDoctorNames] = React.useState(false);
  const [loadingDoctorPosition, setLoadingDoctorPosition] = React.useState(false);
  const { handleSubmit, watch, errors, control, formState, setValue, getValues, setError, trigger } = useForm({ defaultValues: preloadedValues });
  const { authStore } = useSelector((state: RootState) => state);
  const [fromAddress, setFromAddress] = useState({ primaryText: "", secondaryText: "" });
  const [toAddress, setToAddress] = useState({ primaryText: "", secondaryText: "" });
  const [medicationRoutes, setMedicationRoutes] = useState<SelectItem[]>([]);
  const [medicationUnits, setMedicationUnits] = useState<SelectItem[]>([]);
  const [requiredFields, setRequiredFields] = useState({
    authorisedDoctor: !authStore.data?.IsAdmin ? true : false,
    confirmInfo: !authStore.data?.IsAdmin ? true : false,
    DeclarationFirstName: !authStore.data?.IsAdmin ? true : false,
    DeclarationLastName: !authStore.data?.IsAdmin ? true : false,
    DeclarationPhone: false,
    DeclarationAHPRANumber: false,
  });
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(false);

  const _setRequiredFields = (field: object) => {
    setRequiredFields((value) => ({ ...value, ...field }));
  };

  const getMeidcationRoutes = async (): Promise<void> => {
    try {
      const medicationRoutes = localStorage.getItem("medicationRoutes");
      if (medicationRoutes) {
        setMedicationRoutes(JSON.parse(medicationRoutes));
      } else {
        const res = await api.getRoutes("");
        localStorage.setItem("medicationRoutes", JSON.stringify(res.data.model));
        setMedicationRoutes(res.data.model ?? []);
      }
    } catch {}
  };
  const getMeidcationUnits = async (): Promise<void> => {
    try {
      const medicationUnits = localStorage.getItem("medicationUnits");
      if (medicationUnits) {
        setMedicationUnits(JSON.parse(medicationUnits));
      } else {
        const res = await api.getMedicationUnits("");
        localStorage.setItem("medicationUnits", JSON.stringify(res.data.model));
        setMedicationUnits(res.data.model ?? []);
      }
    } catch {}
  };
  const classes = useStyles();

  const _setFromAddress = async (props: any) => {
    if (props.originGooglePlaceId) {
      setFromAddress((data) => ({
        primaryText: props.primaryName,
        secondaryText: `${props.streetAddress}, ${props.suburb} ${props.state}, ${props.postcode}`,
      }));
    } else if (props.originCustomerLocationId) {
      const locationData = await getLocationsData();
      const primaryText = locationData.find((item: any) => item.value == props.originCustomerLocationId)?.text;
      let secondaryText = "";
      if (data.originWardId) {
        const wardsData = await getWardsData(props.originCustomerLocationId);
        const fromWardName = wardsData && wardsData.find((item: any) => item.value == data.originWardId)?.text;
        secondaryText = `${fromWardName ? fromWardName : ""}${data.originBed ? ", Bed " + data.originBed : ""}`;
      } else if (data.originBed) {
        secondaryText = "Bed " + data.originBed;
      }
      setFromAddress((data) => ({ primaryText, secondaryText }));
    }
  };

  const _setToAddress = async (props: any) => {
    if (props.destinationGooglePlaceId) {
      setToAddress((data) => ({
        primaryText: props.primaryName,
        secondaryText: `${props.streetAddress}, ${props.suburb} ${props.state}, ${props.postcode}`,
      }));
    } else if (props.destinationCustomerLocationId) {
      const locationData = await getLocationsData();
      const primaryText = locationData.find((item: any) => item.value == props.destinationCustomerLocationId)?.text;
      let secondaryText = "";
      if (data.destinationWardId) {
        const wardsData = await getWardsData(props.destinationCustomerLocationId);
        const toWardName = wardsData && wardsData.find((item: any) => item.value == data.destinationWardId)?.text;
        secondaryText = `${toWardName ? toWardName : ""}${data.destinationBed ? ", Bed " + data.destinationBed : ""}`;
      } else if (data.destinationBed) {
        secondaryText = "Bed " + data.destinationBed;
      }
      setToAddress((data) => ({ primaryText, secondaryText }));
    }
  };

  const getLocationsData = async (): Promise<any> => {
    try {
      const customerLocations = localStorage.getItem("customerLocations");
      if (customerLocations) {
        return JSON.parse(customerLocations);
      } else {
        const res = await api.getCustomerLocations("");
        localStorage.setItem("customerLocations", JSON.stringify(res.data.model));
        return res.data.model;
      }
    } catch {}
  };

  const getWardsData = async (locationId: string): Promise<any> => {
    try {
      const res = await api.getWardsInLocation("locationId=" + locationId);
      return res.data.model;
    } catch {}
  };

  const setFromToData = async () => {
    try {
      if (data.originGooglePlaceId) {
        await _setFromAddress({
          originGooglePlaceId: data.originGooglePlaceId,
          primaryName: data.originName,
          streetAddress: data.originStreetAddress,
          suburb: data.originSuburb,
          state: data.originState,
          postcode: data.originPostcode,
        });
      } else if (data.originCustomerLocationId) {
        await _setFromAddress({ originCustomerLocationId: data.originCustomerLocationId });
      }
      if (data.destinationGooglePlaceId) {
        await _setToAddress({
          destinationGooglePlaceId: data.destinationGooglePlaceId,
          primaryName: data.destinationName,
          streetAddress: data.destinationStreetAddress,
          suburb: data.destinationSuburb,
          state: data.destinationState,
          postcode: data.destinationPostcode,
        });
      } else if (data.destinationCustomerLocationId) {
        await _setToAddress({ destinationCustomerLocationId: data.destinationCustomerLocationId });
      }
    } catch (e) {
      dispatch(showBookingToast({ open: false, message: "" }));
    }
  };

  React.useEffect(() => {
    getMeidcationRoutes();
    getMeidcationUnits();
    setValue("DeclarationFirstName", data.DeclarationFirstName || data.declarationFirstName || "");
    setValue("DeclarationLastName", data.DeclarationLastName || data.declarationLastName || "");
    setValue("DeclarationPhone", data.DeclarationPhone || data.declarationPhone || "");
    setValue("attendingReferringDoctor", data.AttendingReferringDoctor || data.attendingReferringDoctor || "");
    setValue("doctorPosition", data.DoctorPosition || data.doctorPosition || "");
    
    setFromToData();
  }, [data]);

  const saveChanges = async (update = false) => {
    const isValid = await trigger();
    if (isValid) {
      setSubmitButtonDisabled(true);
      await submitData({ ...data, ...getValues() }, dispatch, history);
      setSubmitButtonDisabled(false);
    }
  };

  React.useEffect(() => {
    saveChangesState[1](() => saveChanges);
  }, [data]);

  const onNextButtonClick = async (): Promise<boolean> => {
    const isValid = await trigger();
    if (isValid) {
      var permissions = authStore.data?.role.split(",");
      setSubmitButtonDisabled(true);
      await submitData({ ...data, ...getValues() }, dispatch, history,permissions);
      setSubmitButtonDisabled(false);
    }
    return isValid;
  };
  const handleDoctorNameChange = React.useMemo(
    () =>
      throttle(async (e: any, value: string): Promise<void> => {
        try {
          setLoadingDoctorNames(true);
          let resp = await api.getDoctorNames("keyword=" + value);
          //if (loadingDoctorNames==true) {
            setDoctorNames(resp.data.model);
          //}
          setLoadingDoctorNames(false);
          
        } catch {
          setLoadingDoctorNames(false);
          setDoctorNames([]);
          setLoadingDoctorNames(false);
        }
      }, 200),
    []
  );
  const getDoctorPosition = async (value: String|null): Promise<void> => {
    setLoadingDoctorPosition(true);
    try {
        let res = await api.getDoctorPosition("keyword=" + value);
        if (res.data.message=="Success") setValue("doctorPosition", res.data.model);
        else setValue("doctorPosition", "");
      } catch {}
    setLoadingDoctorPosition(false);
  };

  const getAge = (transferData: any): number => {
    if (transferData.bYear) {
      return _calculateAge(dateUtil.parse(`${transferData.bYear}-${transferData.bMonth}-${transferData.bDay}`, "yyyy-MM-dd"));
    } else if (transferData.byear) {
      return _calculateAge(dateUtil.parse(`${transferData.byear}-${transferData.bmonth}-${transferData.bday}`, "yyyy-MM-dd"));
    } else {
      return NaN;
    }
  };

  let pickupDropoffTimeStr = "";
  if (data.dropoffAfterTime || data.dropoffBeforeTime) {
    if (data.dropoffAfterTime && data.dropoffBeforeTime) {
      pickupDropoffTimeStr = `Requested dropoff after ${parseTime(data.dropoffAfterTime)} and before ${parseTime(
        data.dropoffBeforeTime
      )} ${_calculateDay(data.bookingDate)}`;
    } else if (data.dropoffAfterTime) {
      pickupDropoffTimeStr = `Requested dropoff after ${parseTime(data.dropoffAfterTime)} ${_calculateDay(data.bookingDate)}`;
    } else if (data.dropoffBeforeTime) {
      pickupDropoffTimeStr = `Requested dropoff before ${parseTime(data.dropoffBeforeTime)} ${_calculateDay(data.bookingDate)}`;
    }
  } else {
    if (data.pickupAfterTime && data.pickupBeforeTime) {
      pickupDropoffTimeStr = `Requested pickup after ${parseTime(data.pickupAfterTime)} and before ${parseTime(
        data.pickupBeforeTime
      )} ${_calculateDay(data.bookingDate)}`;
    } else if (data.pickupAfterTime) {
      pickupDropoffTimeStr = `Requested pickup after ${parseTime(data.pickupAfterTime)} ${_calculateDay(data.bookingDate)}`;
    } else if (data.pickupBeforeTime) {
      pickupDropoffTimeStr = `Requested pickup before ${parseTime(data.pickupBeforeTime)} ${_calculateDay(data.bookingDate)}`;
    }
  }
  return (
    <div style={{ width: "100%", marginBottom: 20, overflow: "hidden", paddingTop: 40 }}>
      <Grid item xs={12} spacing={3} style={{ marginBottom: 15 }}>
        <Typography style={{ fontSize: 36, fontWeight: 900 }}>Review & Submit</Typography>
      </Grid>
      <Divider style={{ height: 1, backgroundColor: " rgba(0, 0, 0, 0.12)", marginBottom: 15 }} />
      <Grid container spacing={0}>
        <Grid item xs={6} spacing={3}>
          <Container style={{ width: "100%" }} maxWidth={false} disableGutters>
            <ListItemText
              classes={{ primary: classes.listItemTextPrimary, secondary: classes.listItemTextSecondary }}
              primary={(data.firstname || data.firstName) + " " + (data.lastname || data.lastName)}
              secondary={`${isNaN(getAge(data)) ? "" : `${getAge(data)} year old`} ${
                data.gender == 3 ? "gender not disclosed" : String(data.genderName).toLowerCase()
              }`}
            />
            <div style={{ display: "flex", alignItems: "center", marginBottom: 40, marginTop: 20 }}>
              <span style={{ fontWeight: 700, marginRight: 10, fontSize: 18, color: "#333333" }}>Priority</span>
              <span
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "1rem",
                  height: "1rem",
                  borderRadius: 3,
                  fontSize: 14,
                  padding: 4,
                  background: "rgb(243, 243, 243)",
                }}
              >
                {data.Priority || data.priority || 0}
              </span>

              {data.returnTrip && <RepeatIcon style={{ marginLeft: 20, marginRight: 10, height: 19, color: "#333333" }} />}
              {data.returnTrip && <span>Return trip</span>}
            </div>
            {/* <SimpleCard heading="FROM" primaryText={data.fromCustomerLocationName ? data.fromCustomerLocationName : ''} /> */}
            <div style={{ width: "90%" }}>
              <SimpleCard
                heading="FROM"
                scheduledDate={data.scheduledDate ? "Scheduled Pickup: " + String(data.scheduledDate).replace("T", " ") : null}
                {...fromAddress}
              />

              <SimpleCard heading="TO" {...toAddress} />
            </div>
            {/* <SimpleCard heading="TO" primaryText={data.toCustomerLocationName ? data.toCustomerLocationName : ''} /> */}
            {data.bookingDate && (
              <>
                <Typography
                  style={{ fontWeight: 700, fontSize: 18, opacity: 0.28, color: "#333333", marginTop: 20 }}
                  variant="button"
                  display="block"
                  gutterBottom
                >
                  BOOKING REQUEST
                </Typography>
                {data.bookingDate !== null ? (
                  <Typography style={{ fontSize: 21, fontWeight: 400, color: "#333333" }} variant="body2" gutterBottom>
                    Date: {new Date(data.bookingDate).toLocaleDateString("en-AU")}
                  </Typography>
                ) : null}
                <Typography style={{ fontSize: 21, fontWeight: 400, color: "#333333" }} variant="body2" gutterBottom>
                  {pickupDropoffTimeStr}
                </Typography>
                <Typography style={{ fontSize: 21, fontWeight: 400, color: "#333333" }} variant="body2" gutterBottom>
                  Reason: {getReason(data.reasonForThisTime, data.otherReasonDetails)}
                </Typography>
              </>
            )}
          </Container>
        </Grid>
        <Grid item xs={6} spacing={3}>
          <Container style={{ width: "100%" }} maxWidth={false} disableGutters>
            <div>
              <List
                subheader={
                  <ListSubheader component="div" style={{ fontWeight: 700, fontSize: 18, color: "#333333", marginTop: 15, opacity: 0.28 }}>
                    NEEDS
                  </ListSubheader>
                }
                dense={true}
              >
                {data.mobility == 1 ? (
                  <NeedListItem key="notambulant" text={"Not Ambulant"} iconUrl={"../../assets/svgs/black/nonambulant.svg"} colorPrimary="#333333" />
                ) : (
                  <NeedListItem key="ambulant" text={"Ambulant"} iconUrl={"../../assets/svgs/black/ambulant.svg"} colorPrimary="#333333" />
                )}
                {data.classification ? <AcuityNeedItem theme={"light"} colorPrimary={"#333333"} key="acuity" text={data.classification} /> : null}
                {(() => {
                  const specialNeeds = localStorage.getItem("specialNeedsMap");
                  const specialNeedsMap = JSON.parse(specialNeeds ? specialNeeds : "{}");
                  if (data.BookingSpecialNeeds && data.BookingSpecialNeeds.length > 0) {
                    return data.BookingSpecialNeeds.map((item: any) => (
                      <NeedListItem
                        colorPrimary="#333333"
                        key={item.SpecialNeedId}
                        text={specialNeedsMap[item.SpecialNeedId].name}
                        iconUrl={specialNeedsMap[item.SpecialNeedId]?.icon}
                      />
                    ));
                  } else if (data.bookingSpecialNeeds && data.bookingSpecialNeeds.length > 0) {
                    return data.bookingSpecialNeeds.map((item: any) => (
                      <NeedListItem
                        colorPrimary="#333333"
                        key={item.specialNeedId}
                        text={specialNeedsMap[item.specialNeedId].name}
                        iconUrl={specialNeedsMap[item.specialNeedId]?.icon}
                      />
                    ));
                  }
                })()}
              </List>
              <div style={{ paddingLeft: 16, marginTop: 10 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#333333", opacity: 0.28, marginTop: 23, marginBottom: 13 }}>WEIGHT</div>
                {!data.weight ? (
                  <div style={{ fontSize: 24, fontWeight: "bold", color: "#333333", marginTop: 10 }}>None Reported</div>
                ) : (
                  <div style={{ fontSize: 24, fontWeight: "bold", color: "#333333", marginTop: 10 }}>
                    {data.weight}KG {data.estimateWeight && "(Estimated)"}
                  </div>
                )}
                {data.weight < 3 || data.weight > 200 || data.GirthIsOver90CM === true ? (
                  <div style={{ fontSize: 18, color: "#E15152", marginTop: 10 }}>Patient dimensions outside the flags</div>
                ) : null}
              </div>
              <div style={{ paddingLeft: 16, marginTop: 10 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#333333", opacity: 0.28, marginTop: 23, marginBottom: 13 }}>MEDICATIONS</div>
                {(data.MedicalConditions &&
                  data.MedicalConditions.filter((o: any) => o.dateAndTime).map(({ name, dose, unit, route, dateAndTime }: any) => {
                    return (
                      <div style={{ flexDirection: "row", alignItems: "center", marginTop: 2, marginBottom: 2, maxWidth: 500 }}>
                        <span style={{ display: "block", fontSize: 21, fontWeight: 700, color: "#333333", height: "100%" }}>
                          {dateAndTime ? dateUtil.format(dateUtil.parse(dateAndTime, "yyyy-MM-dd'T'HH:mm:ss"), "dd/MM/yyyy HH:mm") : ""}
                        </span>
                        <span style={{ fontSize: 21, fontWeight: 400, color: "#333333", height: "100%" }}>
                          {name}
                          {dose
                            ? " " +
                              dose +
                              (unit !== null && unit !== undefined && unit !== "" && medicationUnits.some((u) => u.value == unit)
                                ? medicationUnits.find((u) => u.value == unit)?.text
                                : "")
                            : ""}
                          {route && medicationRoutes.some((r) => r.value == route)
                            ? " via " + medicationRoutes.find((r) => r.value == route)?.text
                            : ""}
                        </span>
                      </div>
                    );
                  })) ||
                  (data.medicalConditions &&
                    data.medicalConditions
                      .filter((o: any) => o.dateAndTime)
                      .map(({ name, dose, unit, route, dateAndTime }: any) => {
                        return (
                          <div style={{ flexDirection: "row", alignItems: "center", marginTop: 2, marginBottom: 2, maxWidth: 500 }}>
                            <span style={{ display: "block", fontSize: 21, fontWeight: 700, color: "#333333", height: "100%" }}>
                              {dateAndTime ? dateUtil.format(dateUtil.parse(dateAndTime, "yyyy-MM-dd'T'HH:mm:ss"), "dd/MM/yyyy HH:mm") : ""}
                            </span>
                            <span style={{ fontSize: 21, fontWeight: 400, color: "#333333", height: "100%" }}>
                              {name}
                              {dose
                                ? " " +
                                  dose +
                                  (unit !== null && unit !== undefined && unit !== "" && medicationUnits.some((u) => u.value == unit)
                                    ? medicationUnits.find((u) => u.value == unit)?.text
                                    : "")
                                : ""}
                              {route && medicationRoutes.some((r) => r.value == route)
                                ? " via " + medicationRoutes.find((r) => r.value == route)?.text
                                : ""}
                            </span>
                          </div>
                        );
                      }))}
                {(!data.MedicalConditions || data.MedicalConditions.filter((o: any) => o.dateAndTime).length == 0) &&
                (!data.medicalConditions || data.medicalConditions.filter((o: any) => o.dateAndTime).length == 0) ? (
                  <div style={{ fontSize: 24, fontWeight: "bold", color: "#333333", marginTop: 10 }}>None Reported</div>
                ) : (
                  ""
                )}
              </div>
              <div style={{ paddingLeft: 16, marginTop: 10 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#333333", opacity: 0.28, marginTop: 23, marginBottom: 13 }}>CONDITIONS</div>
                <div style={{ fontSize: 24, fontWeight: "bold", color: "#333333", marginBottom: 13 }}>
                  {data.patientMedicalConditions || data.PatientMedicalConditions || "None Reported"}
                </div>
              </div>
              <div style={{ paddingLeft: 16, marginTop: 10 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#333333", opacity: 0.28, marginTop: 23, marginBottom: 13 }}>ALLERGIES</div>
                <div style={{ fontSize: 24, fontWeight: "bold", color: "#333333" }}>
                  {data.PatientAllergies || data.patientAllergies || "None Reported"}
                </div>
              </div>
              <div style={{ paddingLeft: 16, marginTop: 10 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#333333", opacity: 0.28, marginTop: 23, marginBottom: 13 }}>OBSERVATIONS</div>
                {(data.Observations &&
                  data.Observations.filter((o: any) => o.dateAndTimel).map((item: any) => {
                    const { dateAndTime, capRefill, pain, pulse, respRate, sP02, temp, avpu, bpSystolic, bpDiastolic, bgl } = item;
                    return (
                      <div style={{ flexDirection: "row", alignItems: "center", marginTop: 2, marginBottom: 2, maxWidth: 500 }}>
                        <span style={{ display: "block", fontSize: 21, fontWeight: 700, color: "#333333", height: "100%" }}>
                          {dateAndTime ? dateUtil.format(dateUtil.parse(dateAndTime, "yyyy-MM-dd'T'HH:mm:ss"), "dd/MM/yyyy'T'HH:mm") : ""}
                        </span>
                        <span style={{ fontSize: 21, fontWeight: 400, color: "#333333", height: "100%" }}>
                          {Object.keys(item)
                            .map(function (k) {
                              if (k == "respRate" && item["respRate"]) return "Resp " + item.respRate;
                              if (k == "pulse" && item["pulse"]) return item.pulse + "bpm";
                              if (k == "bpSystolic" && item["bpSystolic"]) return "BP " + item.bpSystolic;
                              if (k == "bpDiastolic" && item["bpDiastolic"])
                                return item["bpSystolic"] ? "/" + item.bpDiastolic : "BP /" + item.bpDiastolic;
                              if (k == "temp" && item["temp"]) return "Temp " + item.temp + "C";
                              if (k == "sP02" && item["sP02"]) return "SpO2 " + item.sP02 + "%";
                              if (k == "pain" && item.pain>=0 && item["pain"] != "" && item["pain"] != null) return "Pain " + item.pain;
                              if (k == "avpu" && item["avpu"]) return "AVPU " + item.avpu;
                              if (k == "capRefill" && item["capRefill"]) return "Cap Refill " + item.capRefill + "s";
                              if (k == "bgl" && item["bgl"]) return "BGL " + item.bgl;
                            })
                            .filter((s) => s)
                            .join(", ")
                            .replace(", /", "/")}
                        </span>
                      </div>
                    );
                  })) ||
                  (data.observations &&
                    data.observations
                      .filter((o: any) => o.dateAndTime)
                      .map((item: any) => {
                        const { dateAndTime } = item;
                        return (
                          <div style={{ flexDirection: "row", alignItems: "center", marginTop: 2, marginBottom: 2, maxWidth: 500 }}>
                            <span style={{ display: "block", fontSize: 21, fontWeight: 700, color: "#333333", height: "100%" }}>
                              {dateAndTime ? dateUtil.format(dateUtil.parse(dateAndTime, "yyyy-MM-dd'T'HH:mm:ss"), "dd/MM/yyyy HH:mm") : ""}
                            </span>
                            <span style={{ fontSize: 21, fontWeight: 400, color: "#333333", height: "100%" }}>
                              {Object.keys(item)
                                .map(function (k) {                         
                                  if (k == "respRate" && item["respRate"]) return "Resp " + item.respRate;
                                  if (k == "pulse" && item["pulse"]) return item.pulse + "bpm";
                                  if (k == "bpSystolic" && item["bpSystolic"]) return "BP " + item.bpSystolic;
                                  if (k == "bpDiastolic" && item["bpDiastolic"])
                                    return item["bpSystolic"] ? "/" + item.bpDiastolic : "BP /" + item.bpDiastolic;
                                  if (k == "temp" && item["temp"]) return "Temp " + item.temp + "C";
                                  if (k == "sP02" && item["sP02"]) return "SpO2 " + item.sP02 + "%";
                                  if (k == "pain" && item.pain>=0  && item["pain"] != "" && item["pain"] != null) return "Pain " + item.pain;
                                  if (k == "avpu" && item["avpu"]) return "AVPU " + item.avpu;
                                  if (k == "capRefill" && item["capRefill"]) return "Cap Refill " + item.capRefill + "s";
                                  if (k == "bgl" && item["bgl"]) return "BGL " + item.bgl;
                                })
                                .filter((s) => s)
                                .join(", ")
                                .replace(", /", "/")}
                            </span>
                          </div>
                        );
                      }))}
                {(!data.Observations || data.Observations.filter((o: any) => o.dateAndTime).length == 0) &&
                (!data.observations || data.observations.filter((o: any) => o.dateAndTime).length == 0) ? (
                  <div style={{ fontSize: 24, fontWeight: "bold", color: "#333333", marginTop: 10 }}>None Reported</div>
                ) : (
                  ""
                )}
              </div>
              {data.alteredCallingCriteria === false && data.advancedCareDirective === false && data.observationsStatus === true ? (
                <li style={{ fontSize: 18, color: "#E15152", marginLeft: 20 }}>
                  <span style={{ position: "relative", left: -10 }}>Patient has no Altered Calling Criteria</span>
                </li>
              ) : null}
              {data.observationsStatus === true ? (
                <li style={{ fontSize: 18, color: "#E15152", marginLeft: 20 }}>
                  <span style={{ position: "relative", left: -10 }}>Observation outside the flags</span>
                </li>
              ) : null}
            </div>
          </Container>
        </Grid>
      </Grid>
      <Divider style={{ marginTop: 30 }} />
      <div style={{ width: "100%", paddingTop: 20 }}>
        {!authStore.data?.IsAdmin &&
          !authStore?.data?.role?.split(",")?.some((p) => p == consts.Permissions.SMAEditBooking || p == consts.Permissions.SMAMakeBooking) && (
            <FormGroup>
              <Controller
                control={control}
                name="authorisedDoctor"
                render={(props: any) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...props}
                        checked={props.value}
                        onChange={(e) => setValue("authorisedDoctor", e.target.checked)}
                        style={{ padding: 9 }}
                        color="primary"
                        name="jason"
                      />
                    }
                    label="Patient has been assessed by a suitably trained and competent clinician as defined by local policy/regulation"
                  />
                )}
                rules={{ required: requiredFields.authorisedDoctor }}
              />
              {errors.authorisedDoctor && <span style={{ color: "red" }}>Required</span>}
              <Controller
                control={control}
                name="confirmInfo"
                render={(props: any) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...props}
                        checked={props.value}
                        onChange={(e) => setValue("confirmInfo", e.target.checked)}
                        style={{ padding: 9 }}
                        color="primary"
                        name="jason"
                      />
                    }
                    label="I confirm that all the information supplied is true and correct and that any information ommitted is not applicable"
                  />
                )}
                rules={{ required: requiredFields.confirmInfo }}
              />
              {errors.confirmInfo && <span style={{ color: "red" }}>Required</span>}
            </FormGroup>
          )}
        {!authStore.data?.IsAdmin &&
          !authStore?.data?.role?.split(",")?.some((p) => p == consts.Permissions.SMAEditBooking || p == consts.Permissions.SMAMakeBooking) && (
            <div style={{ marginTop: 20 }}>
              <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={2}>
                  <div className={classes.formControlLabel}>First Name</div>
                  <Controller
                    name="DeclarationFirstName"
                    defaultValue=""
                    control={control}
                    as={
                      <TextField
                        variant="outlined"
                        error={errors.DeclarationFirstName}
                        helperText={errors.DeclarationFirstName ? errors.DeclarationFirstName.message : ""}
                      />
                    }
                    rules={{
                      required: requiredFields.DeclarationFirstName && true && "First Name is required",
                    }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <div className={classes.formControlLabel}>Last Name</div>
                  <Controller
                    name="DeclarationLastName"
                    defaultValue=""
                    control={control}
                    as={
                      <TextField
                        variant="outlined"
                        error={errors.DeclarationLastName}
                        helperText={errors.DeclarationLastName ? errors.DeclarationLastName.message : ""}
                      />
                    }
                    rules={{
                      required: requiredFields.DeclarationLastName && true && "Last Name is required",
                    }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <div className={classes.formControlLabel}>Phone</div>
                  <Controller
                    name="DeclarationPhone"
                    type="number"
                    defaultValue=""
                    control={control}
                    as={
                      <TextField
                        variant="outlined"
                        error={errors.DeclarationPhone}
                        helperText={errors.DeclarationPhone ? errors.DeclarationPhone.message : ""}
                      />
                    }
                    rules={{
                      required: false,
                    }}
                  />
                </Grid>
              </Grid>
              </div>)}
              <div className={classes.namePosSection}>
                <Typography
                  style={{ fontWeight: 700, fontSize: 18, opacity: 0.28, color: "#333333", marginTop: 20 }}
                  variant="button"
                  display="block"
                  gutterBottom
                >
                  ASSESSED AS STABLE FOR TRANSPORT BY:
                </Typography>
                <Grid container spacing={3} alignItems="flex-start">
                  <Grid item xs={4}>
                    <div className={classes.formControlLabel}>Name</div>
                    <Controller
                      name="attendingReferringDoctor"
                      control={control}
                      // defaultValue=""
                      render={({ onChange, value, name }) => {
                        return(
                        <Autocomplete
                          freeSolo
                          className={classes.autoCompleteRoot}
                          disableClearable
                          options={doctorNames}
                          // getOptionLabel={(option: any) => option || ""}
                          //noOptionsText="No user found"
                          onChange={(e, value) => {
                            //if(typeof value === 'object'){
                              
                              onChange(value);
                              getDoctorPosition(value);
                              //setValue("doctorPosition", value.title);
                              //}
                        
                          }}
                          onInputChange={(e,value) => {
                            setValue("attendingReferringDoctor", value);
                            handleDoctorNameChange(e, value);
                            //onChange(value);
                          }}
                          // value={
                          //   doctorNames.find((item) => item) || {name:value||'',title:""}//.name === value) || {name:value||'',title:""}
                          // }
                          value={value}
                          loading={loadingDoctorNames}
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              fullWidth
                              name={name}
                              style={{ maxWidth: 300 }}
                              margin="normal"
                              variant="outlined"
                              placeholder="Doctor Name"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <React.Fragment>
                                    {loadingDoctorNames ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                  </React.Fragment>
                                ),
                              }}
                            />
                          )}
                          renderOption={(option, { inputValue }) => {
                            return (
                              <div>
                                <Typography color="textPrimary" component={"span"}>
                                  {option}
                                </Typography>{" "}
                                {/* {" "} 
                                <Typography color="textSecondary" component={"span"} style={{textAlign:"right"}}>
                                  {option.title}
                                </Typography> */}
                              </div>
                            );
                          }}
                        />
                      )}}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <div className={classes.formControlLabel}>Position</div>
                    <Controller
                      name="doctorPosition"
                      // defaultValue={""}
                      control={control}
                      render={({ onChange, value, name }) => (
                        <TextField
                          fullWidth
                          variant="outlined"
                          onChange={(e)=>{
                            onChange(e);
                          }}
                          value={value}
                          placeholder="Doctor Position"
                          InputProps={{
                            endAdornment: (
                              <React.Fragment>
                                {loadingDoctorPosition ? <CircularProgress color="inherit" size={20} /> : null}
                              </React.Fragment>
                            ),
                          }}
                          //error={errors.DoctorPosition}
                          //helperText={errors.DoctorPosition ? errors.DoctorPosition.message : ""}
                        />
                      )}
                     
                    />
                  </Grid>
                  {/*<Grid item xs={3}>
                            <div className={classes.formControlLabel}>AHPRA registration number</div>
                            <Controller
                                name='DeclarationAHPRANumber'
                                type="number"
                                defaultValue=''
                                control={control}
                                render={({ onChange, onBlur, value, name, ref }: any) => (<TextField onBlur={onBlur}
                                    value={value}
                                    inputRef={ref}
                                    onChange={(props) => {
                                        onChange(props);
                                        if (value != '') {
                                            _setRequiredFields({
                                                DeclarationFirstName: false,
                                                DeclarationLastName: false,
                                                DeclarationAHPRANumber: true,
                                            })
                                        } else {
                                            _setRequiredFields({
                                                DeclarationFirstName: true,
                                                DeclarationLastName: true,
                                                DeclarationAHPRANumber: false,
                                            })
                                        }
                                    }}
                                    variant='outlined' error={errors.DeclarationAHPRANumber} helperText={errors.DeclarationAHPRANumber ? errors.DeclarationAHPRANumber.message : ""} />)}
                                rules={{
                                    required: requiredFields.DeclarationAHPRANumber && true && "AHPRA Number is required",
                                }}
                            />
                        </Grid>*/}
                </Grid>
              </div>
              {/* <TextField style={{ width: 150, marginRight: 20 }} size="small" label={"First Name"} variant="outlined" />
                    <TextField style={{ width: 150, marginRight: 20 }} size="small" label={"Last Name"} variant="outlined" />
                    <TextField style={{ width: 200, marginRight: 20 }} size="small" label={"Phone"} variant="outlined" />
                    <TextField style={{ width: 300, marginRight: 20 }} size="small" label={"AHPRA registration number"} variant="outlined" /> */}
            {/* </div>
          )} */}
        <div style={{ marginTop: 20 }}>
          {window.location.href.indexOf("editbooking") >= 0 ? null : (
            <Button
              disabled={submitButtonDisabled}
              classes={{
                root: classes.submitButtonRoot,
                label: classes.submitButtonLabel,
              }}
              onClick={onNextButtonClick}
            >
              Submit Booking
            </Button>
          )}
          {/* <Button id="update_changes" style={{ display: "none" }} onClick={() => saveChanges(true)}></Button>
                    <Button id="save_changes" style={{ display: "none" }} onClick={() => saveChanges(false)}></Button> */}
        </div>
      </div>
    </div>
  );
};

const needsStyles = (colorPrimary: string) => {
  return makeStyles((theme: Theme) =>
    createStyles({
      needsPoint: {
        fontSize: 19,
        fontWeight: 400,
        color: colorPrimary,
      },
    })
  )();
};

const NeedListItem = ({ text, iconUrl, colorPrimary }: any) => {
  const classes = needsStyles(colorPrimary);
  let icon = require("../../../assets/svgs/black/" +
    (iconUrl ? iconUrl.split("/")[iconUrl.split("/").length - 1]?.split(".")[0] : "activity") +
    ".svg");
  return (
    <ListItem style={{ paddingTop: 0 }}>
      <ListItemIcon style={{ minWidth: 30, justifyContent: "center" }}>
        {iconUrl?.length > 0 ? <img height={20} width={20} src={icon} /> : null}
      </ListItemIcon>
      <ListItemText style={{ marginLeft: 10 }} classes={{ primary: classes.needsPoint }} primary={text} />
    </ListItem>
  );
};

const AcuityNeedItem = ({ text, colorPrimary, theme }: any) => {
  const acuityMap: any = {
    1: "B",
    2: "C",
    3: "D",
    4: "E",
    5: "H",
    6: "M",
    7: "L",
  };
  let acuity = acuityMap[text] ?? "";

  const classes = needsStyles(colorPrimary);

  return (
    <ListItem style={{ paddingTop: 0 }}>
      <ListItemIcon style={{ minWidth: 30, justifyContent: "center" }}>
        <span
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: 20,
            height: 20,
            borderRadius: 3,
            fontSize: 14,
            color: theme == "dark" ? "#000" : "#fff",
            background: theme == "dark" ? "#FFF" : "#000",
          }}
        >
          {acuity}
        </span>
      </ListItemIcon>
      <ListItemText style={{ marginLeft: 10 }} classes={{ primary: classes.needsPoint }} primary={`Acuity ${acuity}`} />
    </ListItem>
  );
};

function SimpleCard({ heading, primaryText, secondaryText, scheduledDate }: any) {
  return (
    <Card style={{ backgroundColor: "#F3F3F3", marginTop: 10, marginBottom: 10, boxShadow: "none" }}>
      <CardContent>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Typography style={{ fontSize: 18, color: "#333333", opacity: 0.28 }} color="textSecondary" gutterBottom>
            {heading}
          </Typography>

          <Typography style={{ fontSize: 18, color: "#333333" }} color="textSecondary" gutterBottom>
            {scheduledDate}
          </Typography>
        </div>

        <Typography component="div" style={{ fontSize: 24, fontWeight: 700 }}>
          {primaryText}
        </Typography>
        <Typography component="div" style={{ marginTop: -3, fontSize: 21, fontWeight: 400 }}>
          {secondaryText}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default ReviewAndSubmit;
